<script setup>
import { computed, ref } from 'vue'
import TurnstileWidget from '@/components/TurnstileWidget.vue'
import { RouterLink } from 'vue-router'
import { ScanLine } from 'lucide-vue-next'
import { FRONTEND_VERSION } from '@/version'
import { GUIDES } from '@/data/guides'

// Stamped by the ops CLI on every deployment (empty in local dev).
const buildId = import.meta.env.VITE_BUILD_ID || ''
const year = new Date().getFullYear()
// Footer shows the first 10 guides; the homepage #guides section lists them all.
const footerGuides = GUIDES.slice(0, 10)
const moreGuides = GUIDES.length > 10
const API = import.meta.env.VITE_API_URL || ''

// ── Contact / feedback form ────────────────────────────────
const feedbackType = ref('feedback')
const message = ref('')
const email = ref('')
const sending = ref(false)
const submitted = ref(false)
const error = ref('')

// Anti-abuse (see cloudformation/backend/formGuard.js): honeypot + Turnstile.
const website = ref('')
const turnstileToken = ref('')
const verifyUnavailable = ref(false)
const turnstile = ref(null)
const TURNSTILE_ON = !!import.meta.env.VITE_TURNSTILE_SITE_KEY
const canSend = computed(() => !TURNSTILE_ON || !!turnstileToken.value || verifyUnavailable.value)

// ── More from Keelara (fleet cross-links; plain follow links) ─
const keelaraApps = [
  { name: 'Keelara', href: 'https://www.keelara.com' },
  { name: 'TrackCV', href: 'https://www.trackcv.com' },
  { name: 'CrushMyInterview', href: 'https://www.crushmyinterview.com' },
  { name: 'EnoughMoneyPlan', href: 'https://www.enoughmoneyplan.com' },
  { name: 'MyPassionJob', href: 'https://www.mypassionjob.com' },
  { name: 'CookSurprise', href: 'https://www.cooksurprise.com' },
  { name: 'MixSurprise', href: 'https://www.mixsurprise.com' },
  { name: 'PromptCueLab', href: 'https://www.promptcuelab.com' },
  { name: 'CraftMyStack', href: 'https://www.craftmystack.com' },
  { name: 'MatchMyTool', href: 'https://www.matchmytool.com' },
  { name: 'CraftMyFlow', href: 'https://www.craftmyflow.com' },
  { name: 'PaidHarbor', href: 'https://www.paidharbor.com' },
]

const placeholders = {
  feedback: 'Describe your feedback, feature request, or suggestion…',
  support: 'Describe the issue you need help with…',
  bug: 'What went wrong? Steps to reproduce help us fix it faster.',
}

async function submitFeedback() {
  if (!message.value.trim() || sending.value) return
  sending.value = true
  error.value = ''
  try {
    const res = await fetch(`${API}/api/feedback`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        message: message.value.trim(),
        email: email.value.trim() || undefined,
        type: feedbackType.value,
        website: website.value,
        turnstileToken: turnstileToken.value || undefined,
      }),
    })
    if (!res.ok) {
      const body = await res.json().catch(() => ({}))
      throw new Error(body.error || `Request failed (${res.status})`)
    }
    submitted.value = true
  } catch (err) {
    error.value = err.message || "Couldn't send — please try again."
  } finally {
    sending.value = false
    turnstile.value?.reset()
  }
}

// ── Website rating ─────────────────────────────────────────
const siteRating = ref(0)
const ratingComment = ref('')
const ratingSending = ref(false)
const ratingSubmitted = ref(
  typeof window !== 'undefined' && window.localStorage?.getItem('psnap:siteRated') === 'true',
)

// Rating card: Turnstile appears once a star is picked (see formGuard.js
// checkRating on the API). A rejected rating shows its error instead of a
// false "thanks".
const ratingToken = ref('')
const ratingVerifyUnavailable = ref(false)
const ratingTurnstile = ref(null)
const ratingError = ref('')
const canRate = computed(() => !!siteRating.value && !ratingSending.value &&
  (!TURNSTILE_ON || !!ratingToken.value || ratingVerifyUnavailable.value))

async function submitRating() {
  if (!canRate.value) return
  ratingSending.value = true
  ratingError.value = ''
  const comment = ratingComment.value.trim()
  const ratingMsg = `Website Rating: ${siteRating.value}/5${comment ? ` — ${comment}` : ''}`
  try {
    const res = await fetch(`${API}/api/feedback`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        message: ratingMsg,
        type: 'rating',
        website: website.value,
        turnstileToken: ratingToken.value || undefined,
      }),
    })
    if (!res.ok) {
      const body = await res.json().catch(() => ({}))
      throw new Error(body.error || `Request failed (${res.status})`)
    }
    ratingSubmitted.value = true
    if (typeof window !== 'undefined') window.localStorage?.setItem('psnap:siteRated', 'true')
  } catch (err) {
    ratingError.value = err.message || "Couldn't send — please try again."
  } finally {
    ratingSending.value = false
    ratingTurnstile.value?.reset()
  }
}
</script>

<template>
  <footer class="border-t border-slate-200 bg-white py-10 text-sm text-slate-600 dark:border-slate-800 dark:bg-slate-950 dark:text-slate-400">
    <!-- ── Contact + Rating cards ─────────────────────────── -->
    <div class="container-wide grid gap-6 md:grid-cols-2">
      <!-- Contact Us -->
      <div class="rounded-xl border border-slate-200 bg-white/60 p-6 dark:border-slate-700 dark:bg-slate-900/60">
        <h3 class="font-display text-base font-semibold text-slate-800 dark:text-slate-100">Contact Us</h3>
        <p class="mt-1 text-sm text-slate-500 dark:text-slate-400">Have feedback, a feature request, or need help? We'd love to hear from you.</p>
        <form v-if="!submitted" class="relative mt-4 space-y-3" @submit.prevent="submitFeedback">
          <select v-model="feedbackType" class="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-800 transition focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100">
            <option value="feedback">Feedback / Feature Request</option>
            <option value="support">Support Question</option>
            <option value="bug">Bug Report</option>
          </select>
          <textarea
            v-model="message"
            rows="3"
            maxlength="2000"
            :placeholder="placeholders[feedbackType]"
            required
            class="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-800 placeholder-slate-400 transition focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100 dark:placeholder-slate-500"
          />
          <input
            v-model="email"
            type="email"
            maxlength="320"
            placeholder="Your email (required for support, optional for feedback)"
            :required="feedbackType === 'support'"
            class="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-800 placeholder-slate-400 transition focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100 dark:placeholder-slate-500"
          />
          <!-- Honeypot: invisible to people, filled by bots; the API discards those silently. -->
          <input v-model="website" type="text" name="website" tabindex="-1" autocomplete="off" aria-hidden="true" class="absolute -left-[10000px] h-px w-px overflow-hidden" />
          <TurnstileWidget ref="turnstile" @token="turnstileToken = $event" @unavailable="verifyUnavailable = true">
            <template #fallback>
              <p class="text-xs text-slate-500 dark:text-slate-400">Human check couldn't load (an ad-blocker may be blocking it). You can still send — if it fails, email hello@papersnap.io.</p>
            </template>
          </TurnstileWidget>
          <div class="flex items-center gap-3">
            <button
              type="submit"
              :disabled="sending || !message.trim() || !canSend"
              class="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white shadow-sm transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <svg v-if="!sending" class="h-4 w-4" viewBox="0 0 20 20" fill="currentColor"><path d="M10.894 2.553a1 1 0 00-1.788 0l-7 14a1 1 0 001.169 1.409l5-1.429A1 1 0 009 15.571V11a1 1 0 112 0v4.571a1 1 0 00.725.962l5 1.428a1 1 0 001.17-1.408l-7-14z" /></svg>
              <svg v-else class="h-4 w-4 animate-spin" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" /><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>
              {{ sending ? 'Sending…' : 'Send' }}
            </button>
            <span v-if="error" class="text-sm text-red-500 dark:text-red-400">{{ error }}</span>
          </div>
        </form>
        <div v-else class="mt-4 flex items-center gap-2 text-sm font-medium text-emerald-600 dark:text-emerald-400">
          <svg class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" /></svg>
          {{ feedbackType === 'support' ? "Thanks — we'll reply to your email shortly." : "Thanks — we've got your message!" }}
        </div>
      </div>

      <!-- Rate Our Website -->
      <div class="rounded-xl border border-slate-200 bg-white/60 p-6 dark:border-slate-700 dark:bg-slate-900/60">
        <h3 class="font-display text-base font-semibold text-slate-800 dark:text-slate-100">Rate Our Website</h3>
        <p class="mt-1 text-sm text-slate-500 dark:text-slate-400">How would you rate your experience with Papersnap?</p>
        <div v-if="!ratingSubmitted" class="mt-4 space-y-3">
          <div class="flex items-center gap-1">
            <button
              v-for="star in 5"
              :key="star"
              type="button"
              class="h-8 w-8 transition-transform hover:scale-125"
              :class="star <= siteRating ? 'text-amber-400' : 'text-slate-300 dark:text-slate-600'"
              :aria-label="`Rate ${star} of 5`"
              @click="siteRating = star"
            >
              <svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" /></svg>
            </button>
            <span v-if="siteRating" class="ml-2 text-sm font-semibold text-slate-600 dark:text-slate-300">{{ siteRating }} / 5</span>
          </div>
          <input
            v-model="ratingComment"
            type="text"
            maxlength="500"
            placeholder="Any thoughts? (optional)"
            class="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-800 placeholder-slate-400 transition focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100 dark:placeholder-slate-500"
          />
          <TurnstileWidget v-if="siteRating" ref="ratingTurnstile" @token="ratingToken = $event" @unavailable="ratingVerifyUnavailable = true">
            <template #fallback>
              <p class="text-xs text-slate-500 dark:text-slate-400">Human check couldn't load (an ad-blocker may be blocking it). You can still send — if it fails, email hello@papersnap.io.</p>
            </template>
          </TurnstileWidget>
          <button
            type="button"
            :disabled="!canRate"
            class="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white shadow-sm transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
            @click="submitRating"
          >
            {{ ratingSending ? 'Sending…' : 'Submit Rating' }}
          </button>
          <p v-if="ratingError" class="text-sm text-red-500 dark:text-red-400">{{ ratingError }}</p>
        </div>
        <div v-else class="mt-4 flex items-center gap-2 text-sm font-medium text-emerald-600 dark:text-emerald-400">
          <svg class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" /></svg>
          Thanks for rating us!
        </div>
      </div>
    </div>

    <!-- ── Link grid ──────────────────────────────────────── -->
    <div class="container-wide mt-10 grid gap-8 sm:grid-cols-2 lg:grid-cols-5">
      <div>
        <RouterLink to="/" class="flex items-center gap-2">
          <span class="flex h-8 w-8 items-center justify-center rounded-lg bg-primary text-white">
            <ScanLine class="h-5 w-5" />
          </span>
          <span class="font-display text-lg font-bold tracking-tight text-slate-900 dark:text-white">
            Papersnap
          </span>
        </RouterLink>
        <p class="mt-3 max-w-xs text-xs">Snap a document. Get your data.</p>
      </div>

      <div>
        <h3 class="mb-3 text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-300">Product</h3>
        <ul class="space-y-2">
          <li><RouterLink to="/" class="hover:text-primary">Home</RouterLink></li>
          <li><RouterLink to="/pricing" class="hover:text-primary">Pricing</RouterLink></li>
          <li><RouterLink to="/mcp" class="hover:text-primary">MCP for AI agents</RouterLink></li>
          <!-- Static blog outside the SPA — plain anchor, not a RouterLink. -->
          <li><a href="/blog/index.html" class="hover:text-primary">Blog</a></li>
          <li><RouterLink to="/login" class="hover:text-primary">Sign in</RouterLink></li>
          <li><RouterLink to="/signup" class="hover:text-primary">Get started free</RouterLink></li>
        </ul>
      </div>

      <div>
        <h3 class="mb-3 text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-300">Guides</h3>
        <ul class="space-y-2">
          <li v-for="g in footerGuides" :key="g.path"><RouterLink :to="g.path" class="hover:text-primary">{{ g.label }}</RouterLink></li>
          <li v-if="moreGuides"><RouterLink to="/#guides" class="hover:text-primary">All guides</RouterLink></li>
        </ul>
      </div>

      <div>
        <h3 class="mb-3 text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-300">Legal</h3>
        <ul class="space-y-2">
          <li><RouterLink to="/privacy" class="hover:text-primary">Privacy Policy</RouterLink></li>
          <li><RouterLink to="/terms" class="hover:text-primary">Terms of Use</RouterLink></li>
          <li><RouterLink to="/refund" class="hover:text-primary">Refund Policy</RouterLink></li>
          <li><RouterLink to="/support" class="hover:text-primary">Support</RouterLink></li>
        </ul>
      </div>

      <div>
        <h3 class="mb-3 text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-300">Company</h3>
        <p class="text-xs">
          By <a href="https://www.keelara.com" target="_blank" rel="noopener noreferrer" class="hover:text-primary">Keelara LLC</a>
        </p>
        <p class="mt-2 text-xs">© {{ year }} Keelara LLC. All rights reserved.</p>
        <p class="mt-2 text-[10px] uppercase tracking-wider text-slate-400 dark:text-slate-500" :title="buildId ? `Deployed build ${buildId}` : 'Local build'">
          v{{ FRONTEND_VERSION }}<template v-if="buildId"> · {{ buildId }}</template>
        </p>
      </div>
    </div>

    <!-- ── More from Keelara ──────────────────────────────── -->
    <div class="container-wide mt-8 border-t border-slate-200 pt-6 dark:border-slate-800">
      <h3 class="mb-3 text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-300">More from Keelara</h3>
      <ul class="flex flex-wrap gap-x-4 gap-y-2 text-xs">
        <li v-for="app in keelaraApps" :key="app.href">
          <a :href="app.href" target="_blank" rel="noopener" class="hover:text-primary">{{ app.name }}</a>
        </li>
      </ul>
    </div>
  </footer>
</template>
